import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchWithAuth } from "@/lib/api";
import { Layout } from "@/components/layout/Layout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Eye, Mail, RefreshCw } from "lucide-react";

interface ClientEmailLogEntry {
  id: string;
  account_id: string | null;
  recipient_email: string;
  subject: string;
  html_body: string | null;
  resend_email_id: string | null;
  email_type: string;
  status: string;
  sent_at: string;
  accounts?: { account_name: string } | null;
}

const TYPE_LABELS: Record<string, string> = {
  portal_invite: "Portal Invite",
  magic_link: "Sign-in Link",
  task_notification: "Task Notification",
  auto_reminder: "Auto Reminder",
  automation: "Automation",
};

const TYPE_COLORS: Record<string, string> = {
  portal_invite: "bg-purple-100 text-purple-800 border-purple-200",
  magic_link: "bg-indigo-100 text-indigo-800 border-indigo-200",
  task_notification: "bg-sky-100 text-sky-800 border-sky-200",
  auto_reminder: "bg-amber-100 text-amber-800 border-amber-200",
  automation: "bg-teal-100 text-teal-800 border-teal-200",
};

const STATUS_COLORS: Record<string, string> = {
  sent: "bg-slate-100 text-slate-700 border-slate-200",
  delivered: "bg-green-100 text-green-800 border-green-200",
  bounced: "bg-red-100 text-red-800 border-red-200",
  complained: "bg-orange-100 text-orange-800 border-orange-200",
  failed: "bg-red-100 text-red-800 border-red-200",
};

const STATUS_LABELS: Record<string, string> = {
  sent: "Sent",
  delivered: "Delivered",
  bounced: "Bounced",
  complained: "Complained",
  failed: "Failed",
};

const formatRelative = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

function TypeBadge({ type }: { type: string }) {
  const label = TYPE_LABELS[type] ?? type;
  const cls = TYPE_COLORS[type] ?? "bg-gray-100 text-gray-700 border-gray-200";
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border shrink-0 ${cls}`}>
      {label}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const label = STATUS_LABELS[status] ?? status;
  const cls = STATUS_COLORS[status] ?? "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border shrink-0 ${cls}`}>
      {label}
    </span>
  );
}

function EmailRow({
  entry,
  onPreview,
}: {
  entry: ClientEmailLogEntry;
  onPreview: (entry: ClientEmailLogEntry) => void;
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors">
      <Mail className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />

      <div className="flex-1 min-w-0 space-y-0.5">
        <p className="text-sm font-semibold text-foreground leading-snug truncate">{entry.subject}</p>
        <p className="text-xs text-muted-foreground truncate">{entry.recipient_email}</p>
      </div>

      <div className="flex items-center gap-2 shrink-0 ml-2">
        <TypeBadge type={entry.email_type} />
        <StatusBadge status={entry.status} />
        <span className="text-xs text-muted-foreground w-20 text-right">{formatRelative(entry.sent_at)}</span>

        {entry.html_body && (
          <button
            onClick={() => onPreview(entry)}
            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100 transition-colors border border-blue-200"
          >
            <Eye className="w-3 h-3" />
            Preview
          </button>
        )}

        {entry.account_id && (
          <a
            href={`/accounts/${entry.account_id}`}
            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
          >
            Open →
          </a>
        )}
      </div>
    </div>
  );
}

export default function ClientEmailLog() {
  const [previewEntry, setPreviewEntry] = useState<ClientEmailLogEntry | null>(null);
  const [accountFilter, setAccountFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [collapsedAccounts, setCollapsedAccounts] = useState<Set<string>>(new Set());

  const { data: entries = [], isLoading, refetch, isFetching } = useQuery<ClientEmailLogEntry[]>({
    queryKey: ["client-email-log"],
    queryFn: () => fetchWithAuth<ClientEmailLogEntry[]>("/api/client-email-log"),
    staleTime: 60_000,
    refetchInterval: 120_000,
  });

  const toggleAccount = (id: string) => {
    setCollapsedAccounts(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Build account list for dropdown
  const accounts = useMemo(() => {
    const map = new Map<string, string>();
    entries.forEach(e => {
      if (e.account_id) map.set(e.account_id, e.accounts?.account_name ?? e.recipient_email);
    });
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [entries]);

  // Filtered entries
  const filtered = useMemo(() => {
    let list = entries;
    if (accountFilter !== "all") list = list.filter(e => e.account_id === accountFilter);
    if (typeFilter !== "all") list = list.filter(e => e.email_type === typeFilter);
    return list;
  }, [entries, accountFilter, typeFilter]);

  // Group by account
  const grouped = useMemo(() => {
    const map = new Map<string, { name: string; entries: ClientEmailLogEntry[] }>();
    filtered.forEach(e => {
      const key = e.account_id ?? "__unknown__";
      const name = e.accounts?.account_name ?? "Unknown Account";
      const group = map.get(key) ?? { name, entries: [] };
      group.entries.push(e);
      map.set(key, group);
    });
    return [...map.entries()].sort(([, a], [, b]) =>
      new Date(b.entries[0].sent_at).getTime() - new Date(a.entries[0].sent_at).getTime()
    );
  }, [filtered]);

  // Open HTML preview in a blob URL iframe
  const openPreview = (entry: ClientEmailLogEntry) => setPreviewEntry(entry);
  const previewBlobUrl = useMemo(() => {
    if (!previewEntry?.html_body) return null;
    const blob = new Blob([previewEntry.html_body], { type: "text/html" });
    return URL.createObjectURL(blob);
  }, [previewEntry]);

  return (
    <Layout>
      <div className="p-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-bold text-foreground">Sent Email Log</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              All emails sent to clients — portal invites, sign-in links, task notifications, and reminders.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">
              {filtered.length} email{filtered.length !== 1 ? "s" : ""}
            </span>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded border border-border bg-background text-xs font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-3 h-3 ${isFetching ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Filter bar */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <label className="text-sm font-medium text-foreground shrink-0">Filter by account:</label>
          <select
            value={accountFilter}
            onChange={e => { setAccountFilter(e.target.value); setCollapsedAccounts(new Set()); }}
            className="text-sm border border-border rounded-md px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 min-w-[220px]"
          >
            <option value="all">All accounts ({entries.length})</option>
            {accounts.map(([id, name]) => (
              <option key={id} value={id}>
                {name} ({entries.filter(e => e.account_id === id).length})
              </option>
            ))}
          </select>

          <label className="text-sm font-medium text-foreground shrink-0 ml-2">Type:</label>
          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            className="text-sm border border-border rounded-md px-3 py-1.5 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            <option value="all">All types</option>
            {Object.entries(TYPE_LABELS).map(([val, label]) => (
              <option key={val} value={val}>{label}</option>
            ))}
          </select>
        </div>

        {/* Content */}
        {isLoading && (
          <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>
        )}
        {!isLoading && filtered.length === 0 && (
          <div className="py-16 text-center">
            <p className="text-sm font-medium text-foreground">No client emails logged yet.</p>
            <p className="text-xs text-muted-foreground mt-1">
              Emails sent to clients will appear here once the migration is applied and new emails are sent.
            </p>
          </div>
        )}

        <div className="space-y-3">
          {grouped.map(([accountId, group]) => {
            const isCollapsed = collapsedAccounts.has(accountId);
            const mostRecent = group.entries[0].sent_at;
            return (
              <div key={accountId} className="border border-border rounded-lg overflow-hidden bg-card shadow-sm">
                {/* Account header */}
                <button
                  onClick={() => toggleAccount(accountId)}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-muted/50 hover:bg-muted transition-colors text-left"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-foreground">{group.name}</span>
                    <span className="text-xs font-semibold bg-amber-400 text-amber-900 rounded-full px-2 py-0.5">
                      {group.entries.length}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">{formatRelative(mostRecent)}</span>
                    {accountId !== "__unknown__" && (
                      <a
                        href={`/accounts/${accountId}`}
                        onClick={e => e.stopPropagation()}
                        className="text-xs font-medium text-primary hover:underline"
                      >
                        Open account →
                      </a>
                    )}
                    <span className="text-muted-foreground text-xs">{isCollapsed ? "▼" : "▲"}</span>
                  </div>
                </button>

                {!isCollapsed && (
                  <div className="divide-y divide-border">
                    {group.entries.map(entry => (
                      <EmailRow key={entry.id} entry={entry} onPreview={openPreview} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Email preview modal */}
      {previewEntry && previewBlobUrl && (
        <Dialog
          open={true}
          onOpenChange={() => {
            if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
            setPreviewEntry(null);
          }}
        >
          <DialogContent className="max-w-2xl w-full" style={{ height: "80vh", display: "flex", flexDirection: "column" }}>
            <DialogHeader>
              <DialogTitle className="truncate text-sm font-semibold">{previewEntry.subject}</DialogTitle>
              <p className="text-xs text-muted-foreground">To: {previewEntry.recipient_email}</p>
            </DialogHeader>
            <div className="flex items-center gap-2 pb-2 border-b border-border">
              <TypeBadge type={previewEntry.email_type} />
              <StatusBadge status={previewEntry.status} />
              <span className="text-xs text-muted-foreground ml-1">{formatRelative(previewEntry.sent_at)}</span>
            </div>
            <div className="flex-1 overflow-hidden rounded border border-border mt-2">
              <iframe src={previewBlobUrl} title={previewEntry.subject} className="w-full h-full border-0" />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Layout>
  );
}
